import { Button, Card, CardContent, CardHeader, CardTitle } from '@agms/ui'
import {
  EQUIPMENT_ASSET_STATUSES,
  equipmentAssetLabel,
} from '../../pages/merchants/merchantFormatters'
import type { MerchantEquipmentAsset } from '../../pages/merchants/merchantFormatters'

interface EquipmentAssetsPanelProps {
  equipmentAssets?: MerchantEquipmentAsset[]
  canManageEquipmentAssets: boolean
  deletePending: boolean
  onAddAsset: () => void
  onEditAsset: (asset: MerchantEquipmentAsset) => void
  onDeleteAsset: (asset: MerchantEquipmentAsset) => void
}

export function EquipmentAssetsPanel({
  equipmentAssets,
  canManageEquipmentAssets,
  deletePending,
  onAddAsset,
  onEditAsset,
  onDeleteAsset,
}: EquipmentAssetsPanelProps) {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-3">
          <CardTitle>Equipment Assets</CardTitle>
          {canManageEquipmentAssets && (
            <Button
              variant="secondary"
              onClick={onAddAsset}
            >
              Add a New Asset
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <p className="mb-3 text-xs text-gray-500">
          Manual AGMS equipment tracking only. These assets do not update Fiserv.
        </p>
        {equipmentAssets && equipmentAssets.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Type</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Make / Model</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Terminal ID</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Serial</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Tracking</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Status</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Added</th>
                  {canManageEquipmentAssets && (
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Actions</th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {equipmentAssets.map((asset) => (
                  <tr key={asset.id}>
                    <td className="px-3 py-2 font-medium text-gray-900">{equipmentAssetLabel(asset.asset_type)}</td>
                    <td className="px-3 py-2 text-gray-700">
                      {[asset.make, asset.model].filter(Boolean).join(' ') || '—'}
                      {asset.notes && <div className="text-xs text-gray-500">{asset.notes}</div>}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs text-gray-700">{asset.terminal_id || '—'}</td>
                    <td className="px-3 py-2 font-mono text-xs text-gray-700">{asset.serial_number || '—'}</td>
                    <td className="px-3 py-2 font-mono text-xs text-gray-700">{asset.tracking_number || '—'}</td>
                    <td className="px-3 py-2">
                      <span className="inline-flex rounded-full border border-gray-200 bg-gray-50 px-2 py-0.5 text-xs font-semibold capitalize text-gray-700">
                        {EQUIPMENT_ASSET_STATUSES.find((status) => status.value === asset.status)?.label ?? asset.status}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-xs text-gray-500">
                      {asset.created_at ? new Date(asset.created_at).toLocaleDateString() : '—'}
                    </td>
                    {canManageEquipmentAssets && (
                      <td className="px-3 py-2">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="secondary"
                            onClick={() => onEditAsset(asset)}
                          >
                            Edit
                          </Button>
                          <Button
                            variant="secondary"
                            onClick={() => onDeleteAsset(asset)}
                            disabled={deletePending}
                          >
                            Delete
                          </Button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="py-4 text-center text-sm text-gray-500">No equipment assets on file</p>
        )}
      </CardContent>
    </Card>
  )
}
